import { DataGrid, type GridColumn } from "@/components/data-grid";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useState } from "react";

import { PERMISSIONS } from "../lib/permissions.ts";
import { errorMessage } from "../lib/query.ts";
import { analytics } from "../lib/telemetry/analytics.ts";

import { useWorkspace } from "../app/AppShell.tsx";
import { useSession } from "../app/session.tsx";
import { Markdown } from "../chat/components/Markdown.tsx";
import {
  changeLabel,
  fileSize,
  mechanicQuery,
  previewChange,
  statusLabel,
  type MechanicVersionSummary,
} from "./mechanics.ts";

/** Fuera del componente: dentro cambiarían de identidad en cada render. */
const VERSION_COLUMNS: readonly GridColumn<MechanicVersionSummary>[] = [
  { key: "number", label: "#", numeric: true, render: (v) => v.number },
  { key: "status", label: "Estado", render: (v) => statusLabel(v.status) },
  { key: "notes", label: "Qué cambió", render: (v) => v.notes ?? "—" },
  { key: "authoredBy", label: "Quién", render: (v) => v.authoredBy },
];

/**
 * El documento de una mecánica, en el panel central del chat.
 *
 * Vive dentro del modo Chat y no en el panel (§7.3): la lista está en la
 * columna de memoria, de donde se viene, y el documento se lee ANCHO porque es
 * lo que alguien tiene que leer entero antes de aprobarlo. Estar dentro del
 * chat lo mejora: pasárselo al agente ya no obliga a cambiar de pantalla.
 *
 * Editar aquí NO es un atajo. El usuario cambia el DOCUMENTO --que es suyo--,
 * ve el diff por la misma ruta que usaría el agente (`preview_mechanic_change`)
 * y para guardar se lo pasa al chat, porque el código lo deriva el agente del
 * documento. Con un guardado propio habría dos formas de cambiar una mecánica
 * y una se saltaría la trazabilidad.
 */
export function MechanicView() {
  /* La ruta de la sesión y el armazón conversacional no tienen segmento, así
     que sus nombres --`_authenticated` y `chat`-- salen en el ID de la ruta
     aunque no salgan en la URL, que sigue siendo `/memoria/mecanicas/...`. */
  const { period, slug } = useParams({
    from: "/_authenticated/chat/memoria/mecanicas/$period/$slug",
  });
  const { sendToOpenConversation } = useWorkspace();

  const [editing, setEditing] = useState(false);
  /* Editar aquí es redactar una versión nueva para pasársela al agente, que
     la guarda con `mechanics.write`. Sin ese permiso, el documento se lee. */
  const canWrite = useSession().can(PERMISSIONS.mechanicsWrite);
  const [draft, setDraft] = useState("");

  const detail = useQuery(mechanicQuery(period, slug));
  const open = detail.data;

  /**
   * Comparar es una MUTACIÓN aunque no escriba nada.
   *
   * No se cachea por la dirección: lo que se compara es el borrador que el
   * usuario tiene delante, y ese cambia con cada tecla. Guardarlo por clave
   * dejaría enseñando el diff de un texto que ya no es el que hay escrito.
   */
  const preview = useMutation({
    mutationFn: () =>
      previewChange({
        period,
        slug,
        spec: draft,
        // El código NO se toca desde aquí: lo deriva el agente del documento.
        // Se manda el vigente para que el diff sea del documento y solo de él.
        code: open?.version?.code ?? "",
      }),
  });

  /**
   * El documento se lo lleva el chat: el código lo deriva el agente, así que
   * guardar desde aquí sería la segunda vía que este diseño existe para no
   * tener.
   */
  const handOver = () => {
    if (open === undefined) return;
    analytics.mechanic.handedToAgent({ period });
    sendToOpenConversation(
      `He editado el documento de la mecánica ${open.address}. Este es el texto ` +
        `completo como debe quedar; deriva el código, enséñame el diff y dime si ` +
        `cambia el cálculo antes de guardar nada.\n\n---\n\n${draft}`,
    );
  };

  if (detail.isPending) {
    return (
      <p className="p-4 text-sm text-muted-foreground">Cargando la mecánica…</p>
    );
  }

  // Sin datos y sin estar cargando solo queda el fallo, y el del API ya dice
  // si la dirección no existe: repetirlo aquí sería inventarse el motivo.
  if (open === undefined) {
    return (
      <div className="p-4">
        <Alert variant="destructive">
          <AlertDescription>{errorMessage(detail.error)}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <section className="min-h-0 flex-1 overflow-y-auto p-4">
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold">{open.name}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            <span className="font-mono">{open.address}</span>
            {open.version !== null && (
              <>
                {" · v"}
                {String(open.version.number)} {statusLabel(open.version.status)}
              </>
            )}
            {open.inputs.length > 0 && (
              <> · entradas: {open.inputs.join(", ")}</>
            )}
          </p>
        </div>

        {canWrite && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (!editing) analytics.mechanic.editStarted({ period });
                setEditing((on) => !on);
                preview.reset();
                setDraft(open.version?.spec ?? "");
              }}
            >
              {editing ? "Ver el documento" : "Editar"}
            </Button>
            {editing && (
              <Button size="sm" onClick={handOver}>
                Pasárselo al agente
              </Button>
            )}
          </div>
        )}
      </header>

      {editing ? (
        <>
          <Textarea
            className="min-h-96 font-mono text-sm leading-relaxed"
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
              preview.reset();
            }}
            spellCheck={false}
          />

          <div className="my-4 flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                analytics.mechanic.diffPreviewed({ period });
                preview.mutate();
              }}
              disabled={preview.isPending}
            >
              {preview.isPending ? "Comparando…" : "Ver qué cambia"}
            </Button>
          </div>

          {preview.error != null && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{errorMessage(preview.error)}</AlertDescription>
            </Alert>
          )}

          {preview.data !== undefined && (
            <Card className="mb-4 border-l-2 border-l-primary py-4">
              <CardContent className="px-4">
                <p className="text-sm">
                  <b>{changeLabel(preview.data.kind)}.</b> {preview.data.reason}
                </p>
                <pre className="mt-2 overflow-x-auto font-mono text-xs">
                  {preview.data.specDiff}
                </pre>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        // El documento se lee en una columna, no a todo lo ancho de una
        // pantalla grande: una línea de 200 caracteres no se sigue con la
        // vista.
        <Card className="max-w-3xl py-5">
          <CardContent className="px-5">
            <Markdown text={open.version?.spec ?? "_Sin documento._"} />
          </CardContent>
        </Card>
      )}

      <Separator className="my-6" />

      <h3 className="mb-2 font-semibold">Versiones</h3>
      <DataGrid
        columns={VERSION_COLUMNS}
        rows={open.versions}
        empty="Todavía no hay ninguna versión."
      />

      <h3 className="mt-6 mb-2 font-semibold">Adjuntos</h3>
      {open.attachments.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Sin adjuntos. Súbele al agente el archivo del cliente y pídele que lo
          adjunte aquí.
        </p>
      ) : (
        <ul className="grid gap-1">
          {open.attachments.map((attachment) => (
            <li key={attachment.id} className="text-sm">
              <b>{attachment.fileName}</b>{" "}
              <span className="text-muted-foreground">
                {fileSize(attachment.sizeBytes)}
                {attachment.note !== null && ` · ${attachment.note}`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
